import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import {
  History, X, Loader2, AlertTriangle, ExternalLink, Mail, Phone, User,
  RefreshCcw, Building2, Clock, TrendingUp,
} from "lucide-react";

const CLOSED = new Set(["resolved", "closed", "cancelled"]);

const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "resolved", label: "Resolved" },
  { key: "closed", label: "Closed" },
  { key: "30d", label: "Last 30 days" },
];

function Stat({ label, value, testId, tone = "" }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 truncate ${tone || "text-slate-800"}`}>{value}</div>
    </div>
  );
}

/**
 * Reusable customer history panel. Used as an in-dialog right column on the New
 * Ticket form and inside a Sheet drawer on Ticket Detail. Self-fetches the
 * aggregated contact summary and renders summary, insights, filters and the
 * recent-ticket list.
 */
export default function CustomerHistoryPanel({ countryCode, contactNumber, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let abort = false;
    setLoading(true);
    api
      .get("/customers/contact-summary", {
        params: { contact_number: contactNumber, country_code: countryCode, limit: 200 },
      })
      .then((r) => { if (!abort) setData(r.data || null); })
      .catch(() => { if (!abort) setData(null); })
      .finally(() => { if (!abort) setLoading(false); });
    return () => { abort = true; };
  }, [contactNumber, countryCode]);

  const insights = data?.insights || {};
  const customer = data?.customer || {};

  const filtered = useMemo(() => {
    const recent = data?.recent || [];
    if (filter === "all") return recent;
    if (filter === "open") return recent.filter((t) => !CLOSED.has(t.status));
    if (filter === "30d") {
      const cutoff = Date.now() - 30 * 86400000;
      return recent.filter((t) => t.created_at && new Date(t.created_at).getTime() >= cutoff);
    }
    return recent.filter((t) => t.status === filter);
  }, [data, filter]);

  const openTicket = (id) => window.open(`/tickets/${id}`, "_blank", "noopener");

  return (
    <div className="flex flex-col h-full" data-testid="customer-history-panel">
      {/* Header */}
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="w-7 h-7 rounded-lg bg-indigo-50 border border-indigo-100 grid place-items-center shrink-0">
          <History className="w-3.5 h-3.5 text-indigo-600" />
        </div>
        <div className="min-w-0 leading-tight">
          <div className="font-heading text-sm font-semibold text-slate-800 tracking-tight">Customer History</div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold mt-0.5">Snapshot &amp; recent tickets</div>
        </div>
        {onClose && (
          <button type="button" onClick={onClose} className="ml-auto text-slate-400 hover:text-slate-700 rounded p-1 hover:bg-slate-100"
                  data-testid="customer-history-close">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex-1 grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : !data || data.total === 0 ? (
        <div className="flex-1 grid place-items-center text-center px-6 text-sm text-slate-400">
          <div><History className="w-6 h-6 mx-auto mb-2 text-slate-300" />No prior tickets for this customer.</div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Customer summary */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded-full bg-indigo-50 text-indigo-700 grid place-items-center text-xs font-semibold border border-indigo-100">
                <User className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate" data-testid="ch-customer-name">{customer.name || "Customer"}</div>
                <div className="flex items-center gap-3 text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{customer.country_code} {customer.contact_number}</span>
                </div>
              </div>
            </div>
            {customer.email && (
              <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mb-2"><Mail className="w-3 h-3" />{customer.email}</div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Total" value={data.total} testId="ch-total" />
              <Stat label="Open" value={data.open_count} testId="ch-open" tone={data.open_count ? "text-amber-600" : ""} />
              <Stat label="Closed" value={data.closed_count} testId="ch-closed" />
              <Stat label="Last ticket" value={fmtDate(data.last_ticket?.created_at)} testId="ch-last-date" />
              <Stat label="Last status" value={(data.last_resolution_status || "—").replace(/_/g, " ")} testId="ch-last-status" />
              <Stat label="SLA breaches" value={insights.sla_breach_count || 0} testId="ch-sla-breach"
                    tone={insights.sla_breach_count ? "text-rose-600" : ""} />
            </div>
          </div>

          {/* Repeat-complaint warning */}
          {(insights.repeat_complaints || []).map((rc) => (
            <div key={rc.category} className="rounded-lg border border-rose-300 bg-rose-50 p-2.5 flex items-start gap-2"
                 data-testid="repeat-complaint-banner">
              <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
              <div className="text-[11px] text-rose-800">
                Customer has raised <strong>{rc.count}</strong> complaints under <strong>{rc.category}</strong> in the last {rc.window_days} days.
              </div>
            </div>
          ))}

          {/* Quick insights */}
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="ch-insights">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Quick insights</div>
            <div className="space-y-2 text-xs">
              <div className="flex items-start gap-2">
                <TrendingUp className="w-3.5 h-3.5 text-indigo-500 mt-0.5 shrink-0" />
                <div className="flex flex-wrap gap-1">
                  <span className="text-slate-500 mr-1">Frequent:</span>
                  {(insights.frequent_categories || []).length
                    ? insights.frequent_categories.map((c) => (
                        <Badge key={c.category} variant="outline" className="text-[10px] bg-white">{c.category} · {c.count}</Badge>
                      ))
                    : <span className="text-slate-400">—</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <RefreshCcw className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                <span className="text-slate-500">Reopened tickets:</span>
                <span className="font-semibold text-slate-700" data-testid="ch-reopened">{insights.reopened_count || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="text-slate-500">Last department:</span>
                <span className="font-semibold text-slate-700 truncate">{insights.last_department || "—"}</span>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-1.5" data-testid="ch-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                data-testid={`ch-filter-${f.key}`}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                  filter === f.key
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Recent ticket list */}
          <div className="space-y-1.5" data-testid="ch-ticket-list">
            {filtered.length === 0 ? (
              <div className="text-center text-xs text-slate-400 py-6">No tickets match this filter.</div>
            ) : filtered.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => openTicket(t.id)}
                data-testid={`ch-ticket-${t.code}`}
                className="w-full text-left rounded-lg border border-slate-200 bg-white px-3 py-2 hover:border-indigo-300 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] text-slate-500">{t.code}</span>
                  <PriorityBadge value={t.priority} />
                  <StatusBadge value={t.status} />
                  <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-indigo-500 ml-auto" />
                </div>
                <div className="text-xs text-slate-700 mt-1 truncate">{t.subject || t.category || "—"}</div>
                <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
                  <span>{t.category || "—"}</span>
                  {t.department_name && <><span>·</span><span className="truncate">{t.department_name}</span></>}
                  <span className="ml-auto inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{fmtDate(t.created_at)}</span>
                  {t.closed_at && <span className="text-emerald-600">✓ {fmtDate(t.closed_at)}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
